/**
 * Shared finance utilities — amortization, derived charges/revenues, styles.
 * Single source of truth: used by MunicipalFinanceTab, TaxImpactSection, FiscalHealthSection.
 */
import type React from 'react';
import { useMemo } from 'react';
import { useFinanceStore } from '../stores/financeStore';

/* ------------------------------------------------------------------
 * Amortization (MCH2 standard)
 * ------------------------------------------------------------------ */
export interface AmortSlice {
  weight: number;
  duration: number;
}

/**
 * MCH2 amortization map — Geneva RAC durations (Art. 40 al. 7).
 * Each cost item maps to one or more MCH2 asset categories with weight + duration.
 * These durations are the single source of truth for the frontend.
 */
export const AMORT_MAP: Record<string, AmortSlice[]> = {
  infra:    [{ weight: 0.60, duration: 40 }, { weight: 0.40, duration: 30 }], // 60% routes(40yr) + 40% canalisations(30yr)
  ep:       [{ weight: 0.60, duration: 40 }, { weight: 0.40, duration: 10 }], // v11: split infra durable (60%) + éléments courts (40%)
  ecoles:   [{ weight: 1.0, duration: 33 }],   // Bâtiments scolaires (Geneva RAC)
  creches:  [{ weight: 1.0, duration: 33 }],   // Crèches (Geneva RAC)
  co:       [{ weight: 1.0, duration: 33 }],   // Bâtiments administratifs (Geneva RAC)
  sport:    [{ weight: 1.0, duration: 25 }],   // Bâtiments sportifs
  culturel: [{ weight: 1.0, duration: 33 }],   // Bâtiments culturels (Geneva RAC)
  zi:       [{ weight: 0.60, duration: 40 }, { weight: 0.40, duration: 30 }], // Same as infra
};

/* ------------------------------------------------------------------
 * Subsidy-to-cost allocation (MCH2: investment subsidies reduce asset base)
 * Maps each built-in subsidy to the cost category it reduces.
 * Operating subsidies (FDAP, OFAS parascolaire) are NOT included —
 * they flow to revenue and do not reduce the depreciable asset base.
 * ------------------------------------------------------------------ */
export const SUBSIDY_ALLOCATION: Record<string, string> = {
  fidu_ep:    'ep',        // FIDU espaces publics → cost_ep_mix
  pa_ge_ep:   'ep',        // PA GE espaces publics → cost_ep_mix
  fidu_ec:    'ecoles',    // FIDU école → cost_ecoles
  fi_cr:      'creches',   // FI ACG crèche → cost_creches
  ofas_cr:    'creches',   // OFAS/LAAcc crèche → cost_creches
  // fidu_log: not allocated — cash transfer to commune, not asset reduction
  // ofas_para: operating subsidy — flows to revenue
  // fdap: operating subsidy — flows to revenue
};

/* ------------------------------------------------------------------
 * Compute net costs per category (gross minus investment subsidies)
 * MCH2 rule: depreciation is calculated on net cost only
 * ------------------------------------------------------------------ */
export function computeNetCosts(
  grossCosts: Record<string, number>,
  investmentSubsidies: Record<string, number>,
): Record<string, number> {
  const net: Record<string, number> = {};
  for (const [key, gross] of Object.entries(grossCosts)) {
    const subsidy = investmentSubsidies[key] ?? 0;
    net[key] = Math.max(0, gross - subsidy);
  }
  return net;
}

/* ------------------------------------------------------------------
 * Aggregate subsidy amounts by target cost category
 * ------------------------------------------------------------------ */
export function aggregateSubsidiesByCost(
  subsidyAmounts: Record<string, number>,
): Record<string, number> {
  const byCost: Record<string, number> = {};
  for (const [subsidyKey, amount] of Object.entries(subsidyAmounts)) {
    const costKey = SUBSIDY_ALLOCATION[subsidyKey];
    if (costKey && amount > 0) {
      byCost[costKey] = (byCost[costKey] ?? 0) + amount;
    }
  }
  return byCost;
}

/**
 * Compute total annual amortization (MCH2 linear method).
 * If investmentSubsidies is provided, costs are netted first (MCH2 compliance).
 */
export function computeAnnualAmort(
  grossCosts: Record<string, number>,
  investmentSubsidies?: Record<string, number>,
): number {
  const costs = investmentSubsidies
    ? computeNetCosts(grossCosts, investmentSubsidies)
    : grossCosts;
  return Object.entries(costs).reduce((sum, [key, cost]) => {
    const slices = AMORT_MAP[key];
    if (!slices || cost <= 0) return sum;
    return sum + slices.reduce((s, sl) => s + (cost * sl.weight) / sl.duration, 0);
  }, 0);
}

/* ------------------------------------------------------------------
 * Derived charges / revenues hook
 * ------------------------------------------------------------------ */
export interface FinanceDerived {
  chargesPerInhab: number;
  crecheCostPerPlace: number;
  revPerContrib: number;
  revPerEmploi: number;
  fixedRevAnnual: number;
  fdapPerPlace: number;
}

export function useFinanceDerived(): FinanceDerived {
  const exploitation = useFinanceStore((s) => s.exploitation);
  const useForfait = useFinanceStore((s) => s.useForfait);
  const forfait = useFinanceStore((s) => s.forfait);
  const revenus = useFinanceStore((s) => s.revenus);

  const chargesPerInhab = useMemo(() => {
    if (useForfait) return forfait;
    return Object.entries(exploitation)
      .filter(([k]) => k !== 'creches')
      .reduce((s, [, item]) => s + item.val, 0);
  }, [exploitation, useForfait, forfait]);

  const revPerContrib = useMemo(() => {
    return Object.entries(revenus).reduce((sum, [key, item]) => {
      if (key === 'impots_pm' || key === 'impots_nr' || key === 'peréquation' || key === 'autres_rev') return sum;
      return sum + item.val;
    }, 0);
  }, [revenus]);

  const revPerEmploi = useMemo(
    () => (revenus.impots_pm?.val ?? 0) + (revenus.impots_nr?.val ?? 0),
    [revenus],
  );
  const fixedRevAnnual = useMemo(
    () => (revenus['peréquation']?.val ?? 0) + (revenus.autres_rev?.val ?? 0),
    [revenus],
  );
  const crecheCostMemo = useMemo(
    () => exploitation.creches?.val ?? 38000,
    [exploitation],
  );
  const fdapPerPlace = 8719;

  return useMemo(
    () => ({ chargesPerInhab, crecheCostPerPlace: crecheCostMemo, revPerContrib, revPerEmploi, fixedRevAnnual, fdapPerPlace }),
    [chargesPerInhab, crecheCostMemo, revPerContrib, revPerEmploi, fixedRevAnnual],
  );
}

/* ------------------------------------------------------------------
 * Cost map builder (from estimation results)
 * ------------------------------------------------------------------ */
export function buildCostMap(r: {
  cost_infra_mix: number;
  cost_ep_mix: number;
  cost_ecoles: number;
  cost_creches: number;
  cost_co: number;
  cost_sport: number;
  cost_culturel: number;
  zi?: { cost_total: number } | null;
}): Record<string, number> {
  return {
    infra: r.cost_infra_mix,
    ep: r.cost_ep_mix,
    ecoles: r.cost_ecoles,
    creches: r.cost_creches,
    co: r.cost_co,
    sport: r.cost_sport,
    culturel: r.cost_culturel,
    zi: r.zi?.cost_total ?? 0,
  };
}

/* ------------------------------------------------------------------
 * Shared inline styles (dark theme)
 * ------------------------------------------------------------------ */
export const sectionStyle: React.CSSProperties = {
  marginTop: 48,
  marginBottom: 36,
};

export const headerStyle: React.CSSProperties = {
  fontFamily: 'var(--font-heading)',
  fontSize: 22,
  marginBottom: 24,
};

export const subHeaderStyle: React.CSSProperties = {
  fontSize: 14,
  color: 'var(--color-text-muted)',
  textTransform: 'uppercase',
  letterSpacing: 1,
  marginBottom: 16,
  fontFamily: 'var(--font-body)',
  fontWeight: 600,
};

export const inputRowStyle: React.CSSProperties = {
  display: 'flex',
  gap: 24,
  marginBottom: 24,
  flexWrap: 'wrap',
};

export const inputGroupStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
};

export const labelStyle: React.CSSProperties = {
  fontSize: 12,
  color: 'var(--color-text-muted)',
  fontFamily: 'var(--font-body)',
};

export const inputStyle: React.CSSProperties = {
  background: 'rgba(255,255,255,0.06)',
  border: '1px solid rgba(255,255,255,0.12)',
  borderRadius: 6,
  padding: '8px 12px',
  color: 'inherit',
  fontSize: 14,
  fontFamily: 'var(--font-body)',
  width: 200,
};

export const kpiRowStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
  gap: 12,
  marginBottom: 32,
};
